import React from 'react';
import { useNavigate } from 'react-router-dom';
import classes from './Checkout.module.scss';
import Container from '../../components/UI/Container/Container';
import {
  Checkbox,
  Col,
  Divider,
  message,
  Row,
  Space,
  Typography,
  Grid,
  Radio,
  Modal
} from 'antd';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Headline from '../../components/UI/Headline/Headline';
import apex from '../../assets/images/apex-legends-icon-16.jpg';
import { DeleteOutlined } from '@ant-design/icons';
import Button from '../../components/UI/Button/Button';
import { ROUTES } from '../../components/AppRouter/AppRouter';
import { deleteFromCart, getCartSummary } from '../../http/cart';
import parse from 'html-react-parser';
import { createOrder, createTransaction, createBankOrder } from '../../http/orders';
import PaymentInStore from '../PaymentInStore/PaymentInStore';
import BackLink from '../../components/BackLink/BackLink';
import BankSelection from '../../components/BankSelection/BankSelection';
import { getUser } from '../../redux/selectors';
import { getSecurityErrorKey } from '../../utils/getSecurityErrorKey';

const PAYMENT_TYPE_MAP = { store: 'TERMINAL', online: 'CARD', bank: 'BANK' };

const Checkout = ({ cart, user }) => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const breakpoints = Grid.useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();

  // Debug: log user state on every render
  console.log('Checkout render:', { user, hasUser: !!user, userKeys: user ? Object.keys(user) : [] });

  const [checked, setChecked] = React.useState(false);
  const [method, setMethod] = React.useState(null);
  const [step, setStep] = React.useState('checkout');
  const [error, setError] = React.useState('');
  const [orderId, setOrderId] = React.useState(null);
  const [addressAlertVisible, setAddressAlertVisible] = React.useState(false);
  const [selectedBank, setSelectedBank] = React.useState(null);
  const [addressAlertTriggered, setAddressAlertTriggered] = React.useState(false);
  const [showIframe, setShowIframe] = React.useState(false);
  const [paymentUrl, setPaymentUrl] = React.useState('');
  const [summary, setSummary] = React.useState(null);

  React.useEffect(() => {
    if (!method) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    setSummary(null);
    getCartSummary(PAYMENT_TYPE_MAP[method])
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((e) => {
        console.error('Failed to load cart summary', e);
      });
    return () => {
      cancelled = true;
    };
  }, [method, cart]);

  const totalPrice = cart.reduce(
    (acc, product) => acc + product.price * product.quantity,
    0
  );
  const totalAmount = cart.reduce((acc, product) => acc + product.quantity, 0);

  const feeAmount = summary?.feeAmount ?? 0;
  const grandTotal = summary?.totalAmount ?? totalPrice;

  const isAddressComplete = React.useMemo(() => {
    if (!user) return false;
    const requiredFields = ['country', 'city', 'street', 'house', 'zip'];
    const result = requiredFields.every(field => user[field]);

    // Debug info
    console.log('Address check:', {
      user,
      requiredFields,
      fieldValues: requiredFields.map(field => ({ field, value: user[field] })),
      result
    });

    return result;
  }, [user]);

  React.useEffect(() => {
    console.log('Address modal effect:', {
      method,
      isAddressComplete,
      shouldShowModal: method === 'online' && !isAddressComplete,
      user: user ? 'exists' : 'undefined',
      userFields: user ? Object.keys(user).filter(key => ['country', 'city', 'street', 'house', 'zip'].includes(key)) : []
    });

    // Only show address modal for 'online' payment method, not for 'bank'
    if (isAddressComplete) {
      setAddressAlertTriggered(false);
    }
  }, [isAddressComplete]);

  React.useEffect(() => {
    if (method === 'online' && !isAddressComplete) {
      setAddressAlertVisible(true);
      setAddressAlertTriggered(true);
    } else {
      setAddressAlertVisible(false);
    }
  }, [method, isAddressComplete, user]);

  const handleModalCancel = () => {
    setAddressAlertVisible(false);
    if (!isAddressComplete) {
      setMethod(null);
    }
  };

  const onRemove = async (product) => {
    try {
      await dispatch(deleteFromCart(product.id, product.denominationId));
    } catch (error) {
      messageApi.error({
        type: 'error',
        content: error.message ?? 'Failed to remove product from cart'
      });
    }
  };

  const toggleCheck = () => {
    setError('');
    setChecked((prevState) => !prevState);
  };

  const onSubmit = async () => {
    try {
      if (method === 'store') {
        const response = await createOrder({ paymentType: 'TERMINAL' });
        setOrderId(response.data.t_order_id);
        setStep('success');
        dispatch({ type: 'Cart/RESET' });
      } else if (method === 'bank') {
        console.log('Submitting bank payment, selectedBank:', selectedBank);

        if (!selectedBank) {
          messageApi.error({
            type: 'error',
            content: t('pages.checkout.selectBankError')
          });
          return;
        }

        // For bank payments, we don't need address validation since we get country from API
        // if (!isAddressComplete) {
        //   setAddressAlertVisible(true);
        //   return;
        // }

        // Create bank order with payment details
        const bankOrderData = {
          paymentType: "BANK",
          preferredProvider: selectedBank.code,
          preferredCountry: selectedBank.country || "LV"
        };

        console.log('Creating bank order with data:', bankOrderData);

        const response = await createBankOrder(bankOrderData);
        console.log('Bank order response:', response);

        if (response.data && response.data.t_order_id) {
          // Redirect to payment URI if available
          if (response.paymentUri) {
            console.log('Redirecting to payment URI:', response.paymentUri);
            window.location.href = response.paymentUri;
          } else {
            // Fallback to success page if no payment URI
            setOrderId(response.data.t_order_id);
            setStep('success');
            dispatch({ type: 'Cart/RESET' });
          }
        } else {
          messageApi.error({
            type: 'error',
            content: 'Failed to create bank order'
          });
        }
      } else {
          if (!isAddressComplete) {
            setAddressAlertVisible(true);
            return;
          }

          const browserData = {
            browserLanguage: navigator.language,
            browserColorDepth: String(window.screen.colorDepth),
            browserScreenHeight: String(window.screen.height),
            browserScreenWidth: String(window.screen.width),
            browserTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            browserJavaEnabled: false,
            browserJavascriptEnabled: true,
          };
          const response = await createOrder({ paymentType: 'CARD', browserData });
          const paymentUrl = response.paymentUri;

          if (paymentUrl) {
            window.location.href = paymentUrl;
          } else {
            messageApi.error({
              type: 'error',
              content: 'Payment URL not found'
          });
        }
      }
    } catch (error) {
      const securityKey = getSecurityErrorKey(error);
      messageApi.error({
        type: 'error',
        content: securityKey ? t(securityKey) : t('errors.security.fallback'),
        duration: 6,
      });
    }
  };

  const handleFillAddressNow = () => {
    navigate(ROUTES.PROFILE, { state: { openEditAddressModal: true } });
  };

  const handleBankSelect = (bank) => {
    console.log('Bank selected in Checkout:', bank);
    setSelectedBank(bank);
  };

  // Debug: log selectedBank whenever it changes
  React.useEffect(() => {
    console.log('selectedBank changed:', selectedBank);
  }, [selectedBank]);

  return (
    <>
      {step === 'checkout' && (
        <Container style={{ marginTop: 140, marginBottom: 150 }}>
          {contextHolder}

          <Modal
            rootClassName={classes.alertModalOverlay}
            title={t('pages.checkout.addressAlertTitle')}
            open={addressAlertVisible}
            onCancel={handleModalCancel}
            footer={[
              <Button key="fill" type="primary" onClick={handleFillAddressNow}>
                {t('button.fillAddress')}
              </Button>,
              <Button
                key="changePayment"
                type="default"
                onClick={() => {
                  setMethod('store');
                  setAddressAlertVisible(false);
                }}
              >
                {t('button.changePayment')}
              </Button>
            ]}
            maskClosable={false}
          >
            <Typography style={{ fontSize: '18px', textAlign: 'center' }}>
              {t('pages.checkout.addressAlertMessage')}
            </Typography>
          </Modal>

          <BackLink />
          <Headline>{t('pages.checkout.title')}</Headline>
          <Row
            style={{
              width: '100%',
              justifyContent: 'space-between',
              marginTop: 40,
              gap: 30
            }}
          >
            <Col
              xl={12}
              xs={24}
              style={{ display: 'flex', flexDirection: 'column', gap: 30 }}
            >
              <div className={classes.block}>
                <h3 style={{ marginBottom: 10, fontSize: 30 }}>
                  {t('pages.checkout.selectPayment')}
                </h3>
                <Typography
                  style={{
                    fontSize: 20,
                    fontWeight: 400,
                    lineHeight: '23.44px'
                  }}
                >
                  {t('pages.checkout.selectPaymentText')}
                </Typography>
                <Radio.Group
                  style={{ width: '100%' }}
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                >
                  <div className={classes.methods}>
                    <div className={classes.methods__item}>
                      <Radio value="store">
                        {t('pages.checkout.paymentInStore')}
                      </Radio>
                    </div>
                    <div
                      className={`${classes.methods__item} ${
                        addressAlertTriggered ? classes.methods__item__disabled : ''
                      }`}
                    >
                      <Radio value="online" disabled={addressAlertTriggered}>
                        {t('pages.checkout.paymentByCard')}
                      </Radio>
                    </div>
                    <div className={classes.methods__item}>
                      <Radio value='bank'>
                        {t('pages.checkout.bankPayment')}
                      </Radio>
                    </div>
                  </div>
                </Radio.Group>

                {method === 'bank' && (
                  <BankSelection
                    onBankSelect={handleBankSelect}
                    selectedBank={selectedBank}
                  />
                )}
              </div>
              <div className={classes.block}>
                <h3 style={{ marginBottom: 30, fontSize: 30 }}>
                  {t('pages.checkout.tc')}
                </h3>
                <Checkbox
                  checked={checked}
                  onChange={toggleCheck}
                  style={{
                    fontSize: 18,
                    fontWeight: 400,
                    alignItems: 'center',
                    lineHeight: '21.09px'
                  }}
                >
                  {t('pages.checkout.accept')}{' '}
                  <a
                    href='https://tigsipay.com/en/documents'
                    target='_blank'
                    style={{
                      color: 'black',
                      textDecoration: 'underline',
                      lineHeight: '21.09px'
                    }}
                    rel='noreferrer'
                  >
                    {t('pages.checkout.readTc')}
                  </a>
                </Checkbox>
                <Button
                  wide
                  style={{ marginTop: 40, marginBottom: 20 }}
                  disabled={
                    !checked ||
                    !method ||
                    !summary ||
                    cart.length === 0 ||
                    totalAmount > 5 ||
                    totalPrice > 1000 ||
                    (method === 'bank' && !selectedBank)
                  }
                  onClick={onSubmit}
                >
                  {t('pages.checkout.placeOrder')}
                </Button>
                <Typography
                  style={{
                    fontSize: 15,
                    textAlign: 'center',
                    color: 'black',
                    fontWeight: 400
                  }}
                >
                  {parse(t('pages.checkout.privacyNotice'))}
                </Typography>
              </div>
            </Col>
            <Col xl={10} xs={24} className={`${classes.block} ${classes.cart}`}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap'
                }}
              >
                <h3 style={{ fontSize: 30 }}>{t('pages.checkout.order')}</h3>
                <Typography
                  className={classes.editBtn}
                  onClick={() => navigate(ROUTES.CART)}
                >
                  {t('pages.checkout.cartEdit')}
                </Typography>
              </div>

              <Divider
                style={{
                  margin: 0,
                  marginTop: 20,
                  borderBlockStart: '1px solid rgba(88, 66, 113, 1)'
                }}
              />

              <Space
                direction='vertical'
                size={30}
                className={classes.cart__items}
              >
                {cart.map((item) => {
                  return (
                    <div className={classes.cart__item} key={item.id}>
                      <div
                        className={classes.cart__img}
                        onClick={() =>
                          navigate({
                            pathname: ROUTES.PRODUCT_CARD,
                            search: `?product=${item.productId}`
                          })
                        }
                      >
                        <img src={item.imgUrl || apex} alt='' />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 20,
                          justifyContent: 'space-between',
                          flexGrow: 1,
                          paddingInline: 20,
                          flexDirection: breakpoints.xs ? 'column' : 'row'
                        }}
                      >
                        <Space
                          direction='vertical'
                          size={breakpoints.xs ? 10 : 20}
                        >
                          <Typography
                            style={{
                              fontSize: breakpoints.xs ? 18 : 24,
                              lineHeight: '20px',
                              fontWeight: 400,
                              cursor: 'pointer'
                            }}
                            className={classes.cart__title}
                            onClick={() =>
                              navigate({
                                pathname: ROUTES.PRODUCT_CARD,
                                search: `?product=${item.productId}`
                              })
                            }
                          >
                            {i18n.language === 'en' ? item.name : item.name_lv}
                          </Typography>
                          <Typography
                            style={{
                              fontSize: breakpoints.xs ? 16 : 20,
                              lineHeight: '20px',
                              fontWeight: 400
                            }}
                          >
                            {t('cart.nominal')}:{' '}
                            {`${i18n.language === 'en' ? item.denominationName : item.denominationName_lv} EUR ${item.price?.toFixed(2)}`}
                          </Typography>
                          <Typography
                            style={{
                              fontSize: breakpoints.xs ? 16 : 20,
                              lineHeight: '20px',
                              fontWeight: 400,
                              color: '#B8B5B6'
                            }}
                          >
                            {t('cart.item')}: {item.quantity}
                          </Typography>
                        </Space>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            flexShrink: 0,
                            flexDirection: breakpoints.xs ? 'row' : 'column'
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: breakpoints.xs ? 18 : 24,
                              lineHeight: '20px',
                              wordWrap: 'normal',
                              fontWeight: 400
                            }}
                          >
                            EUR {(item.price * item.quantity)?.toFixed(2)}
                          </Typography>
                          <DeleteOutlined
                            onClick={() => onRemove(item)}
                            style={{ fontSize: 20, lineHeight: '20px' }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Space>
              {cart.length === 0 && (
                <Typography
                  style={{
                    fontSize: 20,
                    lineHeight: '20px',
                    fontWeight: 400,
                    color: '#B8B5B6',
                    marginBottom: 40,
                    marginTop: 20
                  }}
                >
                  {t('cart.empty')}
                </Typography>
              )}
              <Divider
                style={{
                  margin: 0,
                  borderBlockStart: '1px solid rgba(88, 66, 113, 1)'
                }}
              />
              <Space
                direction="vertical"
                size={10}
                style={{ width: '100%', marginTop: 40 }}
              >
                <Space
                  style={{
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <Typography
                    style={{
                      fontSize: 20,
                      lineHeight: '20px',
                      fontWeight: 400,
                      color: totalPrice > 1000 ? 'red' : 'inherit'
                    }}
                  >
                    {t('etc.subtotal')}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: 20,
                      lineHeight: '20px',
                      fontWeight: 400,
                      color: totalPrice > 1000 ? 'red' : 'inherit'
                    }}
                  >
                    EUR {totalPrice.toFixed(2)}
                  </Typography>
                </Space>
                {feeAmount > 0 && (
                  <Space
                    style={{
                      justifyContent: 'space-between',
                      width: '100%'
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: 18,
                        lineHeight: '20px',
                        fontWeight: 400,
                        color: '#666'
                      }}
                    >
                      {t('etc.fee.total')}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: 18,
                        lineHeight: '20px',
                        fontWeight: 400,
                        color: '#666'
                      }}
                    >
                      EUR {feeAmount.toFixed(2)}
                    </Typography>
                  </Space>
                )}
                <Space
                  style={{
                    justifyContent: 'space-between',
                    width: '100%',
                    marginTop: 10
                  }}
                >
                  <Typography
                    style={{
                      fontSize: 24,
                      lineHeight: '20px',
                      fontWeight: 700
                    }}
                  >
                    {t('etc.total')}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: 24,
                      lineHeight: '20px',
                      fontWeight: 400
                    }}
                  >
                    EUR {grandTotal.toFixed(2)}
                  </Typography>
                </Space>
              </Space>
              {totalAmount > 5 && (
                <Typography
                  style={{
                    fontSize: 16,
                    lineHeight: '20px',
                    fontWeight: 400,
                    color: 'red',
                    marginTop: 20
                  }}
                >
                  {t('cart.limit.quantity')}
                </Typography>
              )}
              {totalPrice > 1000 && (
                <Typography
                  style={{
                    fontSize: 16,
                    lineHeight: '20px',
                    fontWeight: 400,
                    color: 'red',
                    marginTop: 20
                  }}
                >
                  {t('cart.limit.price')}
                </Typography>
              )}
            </Col>
          </Row>
        </Container>
      )}
      {step === 'success' && (
        <PaymentInStore
          onHome={() => {
            navigate(ROUTES.MAIN);
            setStep('checkout');
          }}
          orderId={orderId}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  activity: state.app.activity,
  products: state.games.products,
  cart: state.games.cart,
  user: getUser(state)
});

export default connect(mapStateToProps)(Checkout);
